import { useEffect, useState } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Copy, Eye, X, GripVertical, BarChart3, ChevronUp, ChevronDown } from "lucide-react";
import BoardAnalyticsDialog from "@/components/BoardAnalyticsDialog";

const ANY = "__any__"; // sentinel for "Any" option (shadcn Select disallows empty string)

const DEFAULT_THEME = {
    primary_color: "#002FA7",
    bg: "#ffffff",
    card_bg: "#ffffff",
    border_color: "#e2e8f0",
    text_color: "#0f172a",
    sub_text_color: "#64748b",
    font_family: "Inter, system-ui, sans-serif",
    meta_font: "ui-monospace, SFMono-Regular, monospace",
    title: "Open roles",
    title_size: "30px",
    title_weight: "900",
    card_title_size: "17px",
    card_title_weight: "800",
    meta_size: "11.5px",
    body_size: "15px",
    body_line_height: "1.7",
    apply_label_size: "12px",
    apply_input_size: "14px",
    card_radius: "0px",
    button_radius: "0px",
};

const FONT_OPTIONS = [
    "Inter, system-ui, sans-serif",
    "Roboto, sans-serif",
    "Open Sans, sans-serif",
    "Lato, sans-serif",
    "Poppins, sans-serif",
    "Montserrat, sans-serif",
    "Source Sans Pro, sans-serif",
    "Nunito, sans-serif",
    "Work Sans, sans-serif",
    "DM Sans, sans-serif",
    "Manrope, sans-serif",
    "Plus Jakarta Sans, sans-serif",
    "IBM Plex Sans, sans-serif",
    "Karla, sans-serif",
    "Rubik, sans-serif",
    "Public Sans, sans-serif",
    "Mulish, sans-serif",
    "Raleway, sans-serif",
    "Playfair Display, serif",
    "Merriweather, serif",
    "Lora, serif",
    "EB Garamond, serif",
    "Crimson Text, serif",
    "Georgia, serif",
    "IBM Plex Serif, serif",
    "JetBrains Mono, monospace",
    "Fira Code, monospace",
    "IBM Plex Mono, monospace",
    "ui-monospace, SFMono-Regular, monospace",
];

const WEIGHT_OPTIONS = [
    ["300", "Light (300)"],
    ["400", "Regular (400)"],
    ["500", "Medium (500)"],
    ["600", "Semibold (600)"],
    ["700", "Bold (700)"],
    ["800", "Extrabold (800)"],
    ["900", "Black (900)"],
];

const TITLE_SIZE_OPTIONS = [
    ["18px", "18"], ["20px", "20"], ["22px", "22"], ["24px", "24"], ["26px", "26"],
    ["28px", "28"], ["30px", "30"], ["32px", "32"], ["36px", "36"], ["40px", "40"],
    ["44px", "44"], ["52px", "52"], ["64px", "64"],
];

const CARD_TITLE_SIZE_OPTIONS = [
    ["13px", "13"], ["14px", "14"], ["15px", "15"], ["16px", "16"], ["17px", "17"],
    ["18px", "18"], ["19px", "19"], ["20px", "20"], ["22px", "22"], ["24px", "24"],
];

const META_SIZE_OPTIONS = [
    ["10px", "10"], ["10.5px", "10.5"], ["11px", "11"], ["11.5px", "11.5"], ["12px", "12"],
    ["12.5px", "12.5"], ["13px", "13"], ["14px", "14"],
];

const BODY_SIZE_OPTIONS = [
    ["12px", "12"], ["13px", "13"], ["14px", "14"], ["15px", "15"], ["16px", "16"],
    ["17px", "17"], ["18px", "18"], ["19px", "19"], ["20px", "20"],
];

const LINE_HEIGHT_OPTIONS = [
    ["1.3", "Tight (1.3)"], ["1.4", "Snug (1.4)"], ["1.5", "Normal (1.5)"],
    ["1.6", "Relaxed (1.6)"], ["1.7", "Comfortable (1.7)"], ["1.8", "Airy (1.8)"], ["2", "Loose (2.0)"],
];

// Fields that can appear on a job card (list view)
export const LIST_FIELD_OPTIONS = [
    ["title", "Title"],
    ["urgency", "Urgency badge"],
    ["location", "Location"],
    ["work_mode", "Work mode"],
    ["employment_type", "Employment type"],
    ["experience", "Experience (yrs)"],
    ["department", "Department"],
    ["industry", "Industry"],
    ["salary", "Salary band"],
    ["skills", "Top skills"],
    ["posted_at", "Posted date"],
    ["apply_cta", "Apply CTA arrow"],
];

// Fields that can appear on the apply / detail page when accessed via this board
export const DETAIL_FIELD_OPTIONS = [
    ["title", "Title"],
    ["company_logo", "Company logo"],
    ["urgency", "Urgency badge"],
    ["location", "Location"],
    ["work_mode", "Work mode"],
    ["employment_type", "Employment type"],
    ["experience", "Experience (yrs)"],
    ["department", "Department"],
    ["industry", "Industry"],
    ["salary", "Salary band"],
    ["skills", "Required skills"],
    ["must_haves", "Must-haves"],
    ["description", "Job description"],
    ["apply_form", "Apply form"],
];

const DEFAULT_LIST_FIELDS = ["title", "urgency", "location", "work_mode", "employment_type", "experience", "department", "skills", "apply_cta"];
const DEFAULT_DETAIL_FIELDS = ["title", "company_logo", "urgency", "location", "work_mode", "employment_type", "experience", "department", "salary", "skills", "must_haves", "description", "apply_form"];

const filterToWire = (f) => {
    // map ANY sentinel back to undefined for the API
    const out = {};
    for (const [k, v] of Object.entries(f || {})) {
        if (v === ANY || v === "" || v === null || v === undefined) continue;
        out[k] = v;
    }
    return out;
};

const filterFromWire = (f) => {
    // map missing fields to ANY sentinel for the UI
    return {
        department: f?.department || "",
        work_mode: f?.work_mode || ANY,
        employment_type: f?.employment_type || ANY,
        client_id: f?.client_id || ANY,
        search: f?.search || "",
    };
};

export default function EmbedBoardsPage() {
    const [items, setItems] = useState([]);
    const [clients, setClients] = useState([]);
    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);
    const [analyticsBoard, setAnalyticsBoard] = useState(null);

    const load = async () => {
        try {
            const [a, b] = await Promise.all([
                api.get("/embed-boards"),
                api.get("/clients"),
            ]);
            setItems(a.data?.items || []);
            setClients(b.data?.items || []);
        } catch (err) { toast.error(formatApiError(err)); }
    };
    useEffect(() => { load(); }, []);

    const remove = async (b) => {
        if (!window.confirm(`Delete "${b.name}"? Existing embeds using this board will stop working.`)) return;
        try {
            await api.delete(`/embed-boards/${b.id}`);
            toast.success("Board deleted"); load();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <>
            <div className="max-w-[1600px] mx-auto p-3 sm:p-5 lg:p-6 space-y-4 sm:space-y-6">
                <div className="flex items-end justify-between gap-3 sm:gap-4 flex-wrap">
                    <div className="min-w-0">
                        <div className="text-xs uppercase tracking-widest font-mono text-ikb">Embed boards</div>
                        <h1 className="font-display font-black text-2xl sm:text-4xl mt-2 leading-tight">Custom job boards</h1>
                        <p className="text-xs sm:text-sm text-muted-foreground mt-2">Build themed, filtered boards with custom tabs and configurable field layouts. Each gets a snippet you can drop on any site.</p>
                    </div>
                    <Button className="rounded-none bg-ikb hover:bg-[#001f7a] text-white w-full sm:w-auto" onClick={() => { setEditing(null); setOpen(true); }} data-testid="embed-board-new">
                        <Plus className="w-4 h-4 mr-2" /> New board
                    </Button>
                </div>

                {items.length === 0 ? (
                    <div className="border border-dashed border-border p-16 text-center" data-testid="embed-boards-empty">
                        <h3 className="font-display font-bold text-xl">No boards yet</h3>
                        <p className="text-sm text-muted-foreground mt-1">Create one to embed on your career site.</p>
                    </div>
                ) : (
                    <div className="grid gap-3" data-testid="embed-boards-list">
                        {items.map((b) => (
                            <BoardRow key={b.id} board={b} onEdit={() => { setEditing(b); setOpen(true); }} onDelete={() => remove(b)} onAnalytics={() => setAnalyticsBoard(b)} />
                        ))}
                    </div>
                )}
            </div>

            <BoardEditor open={open} onClose={() => setOpen(false)} board={editing} clients={clients} onSaved={load} />
            <BoardAnalyticsDialog open={!!analyticsBoard} onClose={() => setAnalyticsBoard(null)} board={analyticsBoard} />
        </>
    );
}

function BoardRow({ board, onEdit, onDelete, onAnalytics }) {
    const snippet = buildSnippet(board);
    const previewUrl = `${window.location.origin}/embed/board-v2/${board.id}`;
    const copy = () => {
        navigator.clipboard.writeText(snippet).then(() => toast.success("Snippet copied"));
    };
    return (
        <div className="border border-border bg-white p-4" data-testid={`board-row-${board.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display font-bold text-base">{board.name}</h3>
                        <span className="text-[10px] font-mono uppercase tracking-widest border border-border bg-white px-2 py-0.5">{board.layout || "list"}</span>
                        {(board.tabs || []).length > 0 && (
                            <span className="text-[10px] font-mono uppercase tracking-widest border border-ikb/30 bg-blue-50 text-ikb px-2 py-0.5">{board.tabs.length} tab{board.tabs.length === 1 ? "" : "s"}</span>
                        )}
                    </div>
                    <div className="text-xs text-foreground/70 mt-1 font-mono flex flex-wrap gap-3">
                        <span>views: <strong>{board.views_count || 0}</strong></span>
                        <span>powered-by clicks: <strong>{board.branding_clicks || 0}</strong></span>
                        <span>board id: <span className="text-foreground">{board.id.slice(0, 8)}…</span></span>
                    </div>
                </div>
                <div className="flex gap-1">
                    <a href={previewUrl} target="_blank" rel="noreferrer" className="p-2 border border-border hover:bg-secondary" title="Preview"><Eye className="w-3.5 h-3.5" /></a>
                    <button onClick={onAnalytics} className="p-2 border border-border hover:bg-secondary" title="Analytics" data-testid={`board-analytics-${board.id}`}><BarChart3 className="w-3.5 h-3.5" /></button>
                    <button onClick={copy} className="p-2 border border-border hover:bg-secondary" title="Copy snippet" data-testid={`board-copy-${board.id}`}><Copy className="w-3.5 h-3.5" /></button>
                    <button onClick={onEdit} className="p-2 border border-border hover:bg-secondary" title="Edit" data-testid={`board-edit-${board.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={onDelete} className="p-2 border border-border hover:bg-rose-50 hover:text-rose-700" title="Delete" data-testid={`board-delete-${board.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
            </div>
            <details className="mt-3">
                <summary className="text-[11px] font-mono uppercase tracking-widest text-foreground/70 cursor-pointer">Snippet</summary>
                <pre className="mt-2 bg-secondary border border-border p-3 text-[11px] font-mono overflow-x-auto whitespace-pre-wrap">{snippet}</pre>
            </details>
        </div>
    );
}

function buildSnippet(board) {
    const boardId = board?.id || "";
    const sizing = board?.embed_sizing || "dynamic";
    const attrs = [`data-portalwiz-board="${boardId}"`];
    if (sizing === "fixed") {
        attrs.push(`data-portalwiz-sizing="fixed"`);
        attrs.push(`data-portalwiz-height="${board?.embed_fixed_height || 720}"`);
        attrs.push(`data-portalwiz-width="${board?.embed_fixed_width || "100%"}"`);
    }
    return `<div id="portalwiz-board-${boardId.slice(0, 8)}"></div>
<script src="${API}/public/embed/portalwiz-board.js" ${attrs.join(" ")} defer></script>`;
}

function emptyBoard() {
    return {
        name: "",
        filters: filterFromWire({}),
        theme: { ...DEFAULT_THEME },
        layout: "list",
        show_powered_by: true,
        tabs: [],
        list_fields: [...DEFAULT_LIST_FIELDS],
        detail_fields: [...DEFAULT_DETAIL_FIELDS],
        apply_mode: "quick",
        apply_form_fields: [],
        talent_pool: { enabled: true, title: "", subtitle: "", cta_label: "", form_title: "", form_subtitle: "" },
        embed_sizing: "dynamic",
        embed_fixed_height: 720,
        embed_fixed_width: "100%",
        apply_layout: "two_panel",
        show_highlight_colors: true,
        spotlight_section_on_top: false,
    };
}

function BoardEditor({ open, onClose, board, clients, onSaved }) {
    const [form, setForm] = useState(emptyBoard);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) {
            if (board) {
                setForm({
                    ...board,
                    filters: filterFromWire(board.filters),
                    theme: { ...DEFAULT_THEME, ...(board.theme || {}) },
                    tabs: (board.tabs || []).map((t) => ({ ...t, filters: filterFromWire(t.filters) })),
                    list_fields: board.list_fields?.length ? board.list_fields : [...DEFAULT_LIST_FIELDS],
                    detail_fields: board.detail_fields?.length ? board.detail_fields : [...DEFAULT_DETAIL_FIELDS],
                    apply_mode: board.apply_mode || "quick",
                    apply_form_fields: board.apply_form_fields || [],
                    talent_pool: { enabled: true, title: "", subtitle: "", cta_label: "", form_title: "", form_subtitle: "", ...(board.talent_pool || {}) },
                    embed_sizing: board.embed_sizing || "dynamic",
                    embed_fixed_height: board.embed_fixed_height || 720,
                    embed_fixed_width: board.embed_fixed_width || "100%",
                    apply_layout: board.apply_layout || "two_panel",
                    show_highlight_colors: board.show_highlight_colors !== false,
                    spotlight_section_on_top: !!board.spotlight_section_on_top,
                });
            } else {
                setForm(emptyBoard());
            }
        }
    }, [board, open]);

    const setTheme = (k, v) => setForm((f) => ({ ...f, theme: { ...f.theme, [k]: v } }));
    const setFilter = (k, v) => setForm((f) => ({ ...f, filters: { ...f.filters, [k]: v } }));

    const addTab = () => {
        setForm((f) => ({
            ...f,
            tabs: [...(f.tabs || []), { id: `tab-${Date.now()}`, title: "New tab", filters: filterFromWire({}) }],
        }));
    };
    const updateTab = (i, patch) => {
        setForm((f) => {
            const tabs = [...(f.tabs || [])];
            tabs[i] = { ...tabs[i], ...patch };
            return { ...f, tabs };
        });
    };
    const updateTabFilter = (i, k, v) => {
        setForm((f) => {
            const tabs = [...(f.tabs || [])];
            tabs[i] = { ...tabs[i], filters: { ...(tabs[i].filters || {}), [k]: v } };
            return { ...f, tabs };
        });
    };
    const removeTab = (i) => {
        setForm((f) => ({ ...f, tabs: (f.tabs || []).filter((_, idx) => idx !== i) }));
    };
    const moveTab = (i, dir) => {
        setForm((f) => {
            const tabs = [...(f.tabs || [])];
            const j = i + dir;
            if (j < 0 || j >= tabs.length) return f;
            [tabs[i], tabs[j]] = [tabs[j], tabs[i]];
            return { ...f, tabs };
        });
    };

    const toggleField = (kind, key) => {
        const fieldKey = kind === "list" ? "list_fields" : "detail_fields";
        setForm((f) => {
            const cur = f[fieldKey] || [];
            return { ...f, [fieldKey]: cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key] };
        });
    };

    const submit = async () => {
        if (!form.name.trim()) { toast.error("Name is required"); return; }
        setBusy(true);
        try {
            const payload = {
                name: form.name.trim(),
                filters: filterToWire(form.filters),
                theme: form.theme,
                layout: form.layout,
                show_powered_by: !!form.show_powered_by,
                tabs: (form.tabs || []).map((t) => ({
                    id: t.id || `tab-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                    title: (t.title || "").trim() || "Untitled",
                    filters: filterToWire(t.filters),
                })),
                list_fields: form.list_fields,
                detail_fields: form.detail_fields,
                // Phase 27 — these two were silently dropped in Phase 20 because the
                // payload object didn't include them. The recruiter would flip Quick
                // → Detailed, hit Save, and the dropdown would reset on next open.
                apply_mode: form.apply_mode || "quick",
                apply_form_fields: form.apply_form_fields || [],
                talent_pool: form.talent_pool || { enabled: true },
                embed_sizing: form.embed_sizing || "dynamic",
                embed_fixed_height: Number(form.embed_fixed_height) || 720,
                embed_fixed_width: form.embed_fixed_width || "100%",
                apply_layout: form.apply_layout || "two_panel",
                show_highlight_colors: !!form.show_highlight_colors,
                spotlight_section_on_top: !!form.spotlight_section_on_top,
            };
            if (board) await api.patch(`/embed-boards/${board.id}`, payload);
            else await api.post("/embed-boards", payload);
            toast.success(board ? "Board saved" : "Board created");
            onSaved(); onClose();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const t = form.theme;

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="rounded-none max-w-5xl w-[95vw] max-h-[92vh] overflow-y-auto" data-testid="board-editor">
                <DialogHeader>
                    <DialogTitle>{board ? "Edit board" : "New embed board"}</DialogTitle>
                    <DialogDescription>Configure the board's filters, sub-tabs, fields and theme. Saved boards are accessible from the snippet you can paste anywhere.</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                    {/* ---------- LEFT: form ---------- */}
                    <div className="space-y-4">
                        <Field label="Board name *">
                            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="rounded-none h-10" data-testid="board-name" placeholder="e.g. Engineering careers" />
                        </Field>

                        <Section title="Default filters (apply to every tab)">
                            <Field label="Department">
                                <Input value={form.filters.department || ""} onChange={(e) => setFilter("department", e.target.value)} className="rounded-none h-10" placeholder="e.g. Engineering" data-testid="board-filter-department" />
                            </Field>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Work mode">
                                    <AnySelect value={form.filters.work_mode} onChange={(v) => setFilter("work_mode", v)} options={[["On-site", "On-site"], ["Hybrid", "Hybrid"], ["Remote", "Remote"]]} testId="board-filter-work-mode" />
                                </Field>
                                <Field label="Employment type">
                                    <AnySelect value={form.filters.employment_type} onChange={(v) => setFilter("employment_type", v)} options={[["Full-time", "Full-time"], ["Part-time", "Part-time"], ["Contract", "Contract"], ["Internship", "Internship"], ["Freelance", "Freelance"]]} testId="board-filter-employment" />
                                </Field>
                            </div>
                            <Field label="Client">
                                <AnySelect value={form.filters.client_id} onChange={(v) => setFilter("client_id", v)} options={clients.map(c => [c.id, c.name])} testId="board-filter-client" />
                            </Field>
                        </Section>

                        <Section title="Embed sizing">
                            <p className="text-[11px] text-foreground/70 -mt-1">Pick how the iframe behaves on the host page. Dynamic grows to fit content; Fixed locks the iframe to a chosen height/width and scrolls inside.</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <Field label="Sizing mode">
                                    <PlainSelect
                                        value={form.embed_sizing || "dynamic"}
                                        onChange={(v) => setForm((f) => ({ ...f, embed_sizing: v }))}
                                        options={[["dynamic", "Dynamic (auto-height)"], ["fixed", "Fixed (height / width)"]]}
                                        testId="board-embed-sizing"
                                    />
                                </Field>
                                {form.embed_sizing === "fixed" && (
                                    <>
                                        <Field label="Iframe height (px)">
                                            <Input
                                                type="number"
                                                min={120}
                                                max={4000}
                                                value={form.embed_fixed_height ?? 720}
                                                onChange={(e) => setForm((f) => ({ ...f, embed_fixed_height: e.target.value }))}
                                                className="rounded-none h-10"
                                                data-testid="board-embed-height"
                                            />
                                        </Field>
                                        <Field label="Iframe width (CSS, e.g. 100% or 800px)">
                                            <Input
                                                value={form.embed_fixed_width ?? "100%"}
                                                onChange={(e) => setForm((f) => ({ ...f, embed_fixed_width: e.target.value }))}
                                                className="rounded-none h-10"
                                                placeholder="100% or 800px"
                                                data-testid="board-embed-width"
                                            />
                                        </Field>
                                    </>
                                )}
                            </div>
                        </Section>

                        <Section title="Sub-tabs (optional)">
                            <p className="text-[11px] text-foreground/70 -mt-1">Each tab pre-filters the visible jobs. Leave empty to skip tabs entirely.</p>
                            <div className="space-y-2">
                                {(form.tabs || []).map((tab, i) => (
                                    <div key={tab.id || i} className="border border-border bg-secondary/40 p-3 space-y-2" data-testid={`board-tab-${i}`}>
                                        <div className="flex items-center gap-2">
                                            <GripVertical className="w-3.5 h-3.5 text-foreground/40" />
                                            <Input value={tab.title} onChange={(e) => updateTab(i, { title: e.target.value })} className="rounded-none h-9" placeholder="Tab title" data-testid={`board-tab-title-${i}`} />
                                            <button type="button" onClick={() => moveTab(i, -1)} disabled={i === 0} className="px-1.5 py-1 border border-border text-xs disabled:opacity-30" title="Move up">↑</button>
                                            <button type="button" onClick={() => moveTab(i, 1)} disabled={i === (form.tabs.length - 1)} className="px-1.5 py-1 border border-border text-xs disabled:opacity-30" title="Move down">↓</button>
                                            <button type="button" onClick={() => removeTab(i)} className="px-1.5 py-1 border border-border text-rose-700 hover:bg-rose-50" title="Remove tab" data-testid={`board-tab-remove-${i}`}><X className="w-3 h-3" /></button>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <Input value={tab.filters?.department || ""} onChange={(e) => updateTabFilter(i, "department", e.target.value)} className="rounded-none h-9 text-xs" placeholder="Department" data-testid={`board-tab-department-${i}`} />
                                            <Input value={tab.filters?.search || ""} onChange={(e) => updateTabFilter(i, "search", e.target.value)} className="rounded-none h-9 text-xs" placeholder="Search keyword (title/skill)" data-testid={`board-tab-search-${i}`} />
                                            <AnySelect value={tab.filters?.work_mode} onChange={(v) => updateTabFilter(i, "work_mode", v)} options={[["On-site", "On-site"], ["Hybrid", "Hybrid"], ["Remote", "Remote"]]} testId={`board-tab-workmode-${i}`} placeholder="Any work mode" />
                                            <AnySelect value={tab.filters?.employment_type} onChange={(v) => updateTabFilter(i, "employment_type", v)} options={[["Full-time", "Full-time"], ["Part-time", "Part-time"], ["Contract", "Contract"], ["Internship", "Internship"]]} testId={`board-tab-emp-${i}`} placeholder="Any employment" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <Button type="button" variant="outline" size="sm" className="rounded-none mt-2" onClick={addTab} data-testid="board-tab-add">
                                <Plus className="w-3.5 h-3.5 mr-1" /> Add tab
                            </Button>
                        </Section>

                        <Section title="Fields shown on each job card (list)">
                            <div className="flex flex-wrap gap-1.5" data-testid="board-list-fields">
                                {LIST_FIELD_OPTIONS.map(([k, label]) => {
                                    const on = (form.list_fields || []).includes(k);
                                    return (
                                        <button
                                            type="button"
                                            key={k}
                                            onClick={() => toggleField("list", k)}
                                            className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${on ? "bg-ikb text-white border-ikb" : "bg-white text-foreground/70 border-border hover:border-ikb"}`}
                                            data-testid={`board-list-field-${k}`}
                                        >
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>
                        </Section>

                        <Section title="Fields shown on the job detail / apply page">
                            <div className="flex flex-wrap gap-1.5" data-testid="board-detail-fields">
                                {DETAIL_FIELD_OPTIONS.map(([k, label]) => {
                                    const on = (form.detail_fields || []).includes(k);
                                    return (
                                        <button
                                            type="button"
                                            key={k}
                                            onClick={() => toggleField("detail", k)}
                                            className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${on ? "bg-ikb text-white border-ikb" : "bg-white text-foreground/70 border-border hover:border-ikb"}`}
                                            data-testid={`board-detail-field-${k}`}
                                        >
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>
                            <p className="text-[10px] font-mono text-foreground/60 mt-2">Note: the apply form itself can be hidden if you only want to surface the JD inside your site.</p>
                        </Section>

                        <Section title="Apply form">
                            <ApplyFormConfigurator
                                applyMode={form.apply_mode || "quick"}
                                onApplyMode={(v) => setForm((f) => ({ ...f, apply_mode: v }))}
                                applyLayout={form.apply_layout || "two_panel"}
                                onApplyLayout={(v) => setForm((f) => ({ ...f, apply_layout: v }))}
                                fields={form.apply_form_fields || []}
                                onFieldsChange={(next) => setForm((f) => ({ ...f, apply_form_fields: next }))}
                            />
                        </Section>

                        <Section title="Talent pool CTA">
                            <p className="text-[11px] text-muted-foreground -mt-1">
                                Show a "Join talent pool" card below the job list. Lets candidates submit
                                a resume for future consideration without leaving your iframe.
                                Requires Talent Pool to be enabled at the Company level.
                            </p>
                            <div className="flex items-center gap-3 text-xs">
                                <label className="inline-flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={form.talent_pool?.enabled !== false}
                                        onChange={(e) => setForm((f) => ({ ...f, talent_pool: { ...(f.talent_pool || {}), enabled: e.target.checked } }))}
                                        data-testid="board-tp-enabled"
                                    />
                                    <span>Show talent pool CTA on this board</span>
                                </label>
                            </div>
                            {form.talent_pool?.enabled !== false && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <Field label="CTA heading">
                                        <Input value={form.talent_pool?.title || ""} onChange={(e) => setForm((f) => ({ ...f, talent_pool: { ...(f.talent_pool || {}), title: e.target.value } }))} placeholder="Don't see a fit?" className="rounded-none h-10" data-testid="board-tp-title" />
                                    </Field>
                                    <Field label="CTA button label">
                                        <Input value={form.talent_pool?.cta_label || ""} onChange={(e) => setForm((f) => ({ ...f, talent_pool: { ...(f.talent_pool || {}), cta_label: e.target.value } }))} placeholder="Join talent pool" className="rounded-none h-10" data-testid="board-tp-cta" />
                                    </Field>
                                    <Field label="CTA subtitle">
                                        <Input value={form.talent_pool?.subtitle || ""} onChange={(e) => setForm((f) => ({ ...f, talent_pool: { ...(f.talent_pool || {}), subtitle: e.target.value } }))} placeholder="Submit your resume — we'll reach out when a matching role opens." className="rounded-none h-10" data-testid="board-tp-sub" />
                                    </Field>
                                    <Field label="Form heading override">
                                        <Input value={form.talent_pool?.form_title || ""} onChange={(e) => setForm((f) => ({ ...f, talent_pool: { ...(f.talent_pool || {}), form_title: e.target.value } }))} placeholder="Join the {company} talent pool" className="rounded-none h-10" data-testid="board-tp-form-title" />
                                    </Field>
                                    <Field label="Form intro override">
                                        <Input value={form.talent_pool?.form_subtitle || ""} onChange={(e) => setForm((f) => ({ ...f, talent_pool: { ...(f.talent_pool || {}), form_subtitle: e.target.value } }))} placeholder="Send us your resume — we'll keep it on file…" className="rounded-none h-10" data-testid="board-tp-form-sub" />
                                    </Field>
                                </div>
                            )}
                        </Section>

                        <Section title="Theme">
                            <Field label="Title shown at top">
                                <Input value={t.title || ""} onChange={(e) => setTheme("title", e.target.value)} className="rounded-none h-10" data-testid="board-theme-title" />
                            </Field>
                            <div className="grid grid-cols-3 gap-3">
                                <ColorField label="Primary" value={t.primary_color} onChange={(v) => setTheme("primary_color", v)} testId="board-theme-primary" />
                                <ColorField label="Background" value={t.bg} onChange={(v) => setTheme("bg", v)} testId="board-theme-bg" />
                                <ColorField label="Card" value={t.card_bg} onChange={(v) => setTheme("card_bg", v)} />
                                <ColorField label="Border" value={t.border_color} onChange={(v) => setTheme("border_color", v)} />
                                <ColorField label="Text" value={t.text_color} onChange={(v) => setTheme("text_color", v)} />
                                <ColorField label="Sub-text" value={t.sub_text_color} onChange={(v) => setTheme("sub_text_color", v)} />
                            </div>
                            <Field label="Body font family">
                                <PlainSelect value={t.font_family} onChange={(v) => setTheme("font_family", v)} options={FONT_OPTIONS.map(f => [f, f.split(",")[0]])} testId="board-theme-font" />
                            </Field>
                            <Field label="Meta font (badges, IDs)">
                                <PlainSelect value={t.meta_font || "ui-monospace, SFMono-Regular, monospace"} onChange={(v) => setTheme("meta_font", v)} options={FONT_OPTIONS.map(f => [f, f.split(",")[0]])} testId="board-theme-meta-font" />
                            </Field>

                            <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 pt-1">Headings</div>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Page title size">
                                    <PlainSelect value={t.title_size || "30px"} onChange={(v) => setTheme("title_size", v)} options={TITLE_SIZE_OPTIONS} testId="board-theme-title-size" />
                                </Field>
                                <Field label="Page title weight">
                                    <PlainSelect value={t.title_weight || "900"} onChange={(v) => setTheme("title_weight", v)} options={WEIGHT_OPTIONS} testId="board-theme-title-weight" />
                                </Field>
                                <Field label="Card title size">
                                    <PlainSelect value={t.card_title_size || "17px"} onChange={(v) => setTheme("card_title_size", v)} options={CARD_TITLE_SIZE_OPTIONS} testId="board-theme-card-title-size" />
                                </Field>
                                <Field label="Card title weight">
                                    <PlainSelect value={t.card_title_weight || "800"} onChange={(v) => setTheme("card_title_weight", v)} options={WEIGHT_OPTIONS} testId="board-theme-card-title-weight" />
                                </Field>
                                <Field label="Meta line size">
                                    <PlainSelect value={t.meta_size || "11.5px"} onChange={(v) => setTheme("meta_size", v)} options={META_SIZE_OPTIONS} testId="board-theme-meta-size" />
                                </Field>
                            </div>

                            <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 pt-1">Job description body (apply page)</div>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Body font size">
                                    <PlainSelect value={t.body_size || "15px"} onChange={(v) => setTheme("body_size", v)} options={BODY_SIZE_OPTIONS} testId="board-theme-body-size" />
                                </Field>
                                <Field label="Body line height">
                                    <PlainSelect value={t.body_line_height || "1.7"} onChange={(v) => setTheme("body_line_height", v)} options={LINE_HEIGHT_OPTIONS} testId="board-theme-body-lh" />
                                </Field>
                            </div>

                            <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 pt-1">Apply form (detail page)</div>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Form label size">
                                    <PlainSelect value={t.apply_label_size || "12px"} onChange={(v) => setTheme("apply_label_size", v)} options={[["10px","10"],["11px","11"],["12px","12"],["13px","13"],["14px","14"],["15px","15"]]} testId="board-theme-apply-label-size" />
                                </Field>
                                <Field label="Form input size">
                                    <PlainSelect value={t.apply_input_size || "14px"} onChange={(v) => setTheme("apply_input_size", v)} options={[["12px","12"],["13px","13"],["14px","14"],["15px","15"],["16px","16"],["17px","17"],["18px","18"]]} testId="board-theme-apply-input-size" />
                                </Field>
                            </div>

                            <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 pt-1">Shape & layout</div>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Card corner">
                                    <PlainSelect value={t.card_radius || "0px"} onChange={(v) => setTheme("card_radius", v)} options={[["0px","Sharp"],["4px","Soft"],["8px","Rounded"],["14px","Pill"],["20px","XL"]]} testId="board-theme-card-radius" />
                                </Field>
                                <Field label="Button / tag corner">
                                    <PlainSelect value={t.button_radius || "0px"} onChange={(v) => setTheme("button_radius", v)} options={[["0px","Sharp"],["4px","Soft"],["8px","Rounded"],["999px","Pill"]]} testId="board-theme-button-radius" />
                                </Field>
                                <Field label="Layout">
                                    <PlainSelect value={form.layout} onChange={(v) => setForm((f) => ({ ...f, layout: v }))} options={[["list", "List"], ["grid", "Grid"]]} testId="board-layout" />
                                </Field>
                                <Field label="Powered by">
                                    <PlainSelect value={form.show_powered_by ? "1" : "0"} onChange={(v) => setForm((f) => ({ ...f, show_powered_by: v === "1" }))} options={[["1", "Show"], ["0", "Hide"]]} />
                                </Field>
                                <Field label="Highlight colours" hint="Show the recruiter-picked colour on spotlight jobs">
                                    <PlainSelect
                                        value={form.show_highlight_colors ? "1" : "0"}
                                        onChange={(v) => setForm((f) => ({ ...f, show_highlight_colors: v === "1" }))}
                                        options={[["1", "Show"], ["0", "Hide"]]}
                                        testId="board-show-highlight-colors"
                                    />
                                </Field>
                                <Field label="Spotlight section" hint="Group highlighted jobs into a dedicated top section">
                                    <PlainSelect
                                        value={form.spotlight_section_on_top ? "1" : "0"}
                                        onChange={(v) => setForm((f) => ({ ...f, spotlight_section_on_top: v === "1" }))}
                                        options={[["0", "Off (float to top of one list)"], ["1", "On (dedicated section)"]]}
                                        testId="board-spotlight-section-on-top"
                                    />
                                </Field>
                            </div>
                        </Section>
                    </div>

                    {/* ---------- RIGHT: live preview ---------- */}
                    <div className="border border-border lg:sticky lg:top-2 self-start" data-testid="board-preview">
                        <div className="bg-secondary px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-foreground/70 border-b border-border">Live preview</div>
                        <ThemePreview theme={t} tabs={form.tabs} listFields={form.list_fields} />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" className="rounded-none" onClick={onClose} data-testid="board-cancel">Cancel</Button>
                    <Button disabled={busy} onClick={submit} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="board-save">
                        {busy ? "Saving…" : board ? "Save board" : "Create board →"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ThemePreview({ theme, tabs = [], listFields = [] }) {
    const t = theme;
    const showField = (k) => !listFields.length || listFields.includes(k);
    return (
        <div style={{ background: t.bg, color: t.text_color, fontFamily: t.font_family, padding: 18, minHeight: 320 }}>
            <div style={{ fontSize: 11, fontFamily: "ui-monospace, monospace", textTransform: "uppercase", letterSpacing: ".18em", color: t.primary_color }}>Open roles at</div>
            <h1 style={{ fontSize: 24, fontWeight: 900, letterSpacing: "-0.02em", margin: "4px 0 14px 0" }}>{t.title || "Open roles"}</h1>
            {tabs.length > 0 && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
                    <span style={{ padding: "5px 11px", fontSize: 10, fontFamily: "ui-monospace, monospace", textTransform: "uppercase", letterSpacing: ".12em", background: t.primary_color, color: "#fff" }}>All</span>
                    {tabs.slice(0, 4).map((tb, i) => (
                        <span key={i} style={{ padding: "5px 11px", fontSize: 10, fontFamily: "ui-monospace, monospace", textTransform: "uppercase", letterSpacing: ".12em", border: `1px solid ${t.border_color}`, color: t.sub_text_color }}>{tb.title || "Tab"}</span>
                    ))}
                </div>
            )}
            {[1, 2].map((i) => (
                <div key={i} style={{ background: t.card_bg, border: `1px solid ${t.border_color}`, padding: 14, marginBottom: 8 }}>
                    {showField("title") && <div style={{ fontSize: 15, fontWeight: 800 }}>Sample job title #{i}</div>}
                    <div style={{ fontSize: 11, color: t.sub_text_color, fontFamily: "ui-monospace, monospace", marginTop: 6 }}>
                        {[
                            showField("location") && "Berlin",
                            showField("work_mode") && "Hybrid",
                            showField("employment_type") && "Full-time",
                            showField("experience") && "3–5 yrs",
                            showField("department") && "Engineering",
                        ].filter(Boolean).join(" · ")}
                    </div>
                    {showField("skills") && (
                        <div style={{ display: "inline-block", marginTop: 8, padding: "3px 8px", border: `1px solid ${t.border_color}`, color: t.sub_text_color, fontSize: 10, fontFamily: "ui-monospace, monospace", textTransform: "uppercase", letterSpacing: ".12em" }}>React</div>
                    )}
                </div>
            ))}
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/80 mb-1 block">{label}</Label>
            {children}
            {hint && <div className="text-[10px] text-muted-foreground mt-1 leading-snug">{hint}</div>}
        </div>
    );
}
function Section({ title, children }) {
    return (
        <div className="border border-border p-3 space-y-3">
            <div className="text-xs font-mono uppercase tracking-widest text-foreground/70">{title}</div>
            {children}
        </div>
    );
}

// "Any" select with sentinel so shadcn doesn't error on empty value
function AnySelect({ value, onChange, options, testId, placeholder = "Any" }) {
    return (
        <Select value={value || ANY} onValueChange={(v) => onChange(v === ANY ? ANY : v)}>
            <SelectTrigger className="rounded-none h-10" data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
            <SelectContent>
                <SelectItem value={ANY}>{placeholder}</SelectItem>
                {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
        </Select>
    );
}

function PlainSelect({ value, onChange, options, testId }) {
    return (
        <Select value={value} onValueChange={onChange}>
            <SelectTrigger className="rounded-none h-10" data-testid={testId}><SelectValue /></SelectTrigger>
            <SelectContent>
                {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
        </Select>
    );
}

function ColorField({ label, value, onChange, testId }) {
    return (
        <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/80 mb-1 block">{label}</Label>
            <div className="flex items-center gap-1.5">
                <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} className="w-8 h-9 border border-border" data-testid={testId} />
                <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="rounded-none h-9 text-xs font-mono" />
            </div>
        </div>
    );
}

/* ─── Apply form configurator ────────────────────────────────────────────
 * Lets the recruiter pick:
 *  - apply_mode: "quick" (current — only Name/Email/Phone/City/Resume)
 *     or "detailed" (Apply button opens a configurable form)
 *  - when "detailed":
 *       • A "Selected fields" list shows fields the recruiter has added IN
 *         THE ORDER they'll appear on the public apply form. Each row has
 *         ↑ / ↓ buttons to reorder and an X to remove. The visibility
 *         dropdown (Optional / Required) sits next to each row.
 *       • A "Add field" picker lists every eligible field not yet selected;
 *         clicking appends it to the selected list with mode=optional.
 */
function ApplyFormConfigurator({ applyMode, onApplyMode, applyLayout, onApplyLayout, fields, onFieldsChange }) {
    const [catalog, setCatalog] = useState(null);
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/screening-fields");
                setCatalog(data);
            } catch { /* viewer is recruiter — 403 not expected */ }
        })();
    }, []);

    const eligibleKeys = catalog?.apply_form_eligible_keys || [];
    const byKey = (catalog?.fields || []).reduce((acc, f) => ({ ...acc, [f.key]: f }), {});
    const selected = (fields || []).filter((f) => f?.key && (f.mode === "optional" || f.mode === "required"));
    const selectedKeys = new Set(selected.map((f) => f.key));
    const available = eligibleKeys.filter((k) => !selectedKeys.has(k));

    const setMode = (key, mode) => {
        const next = selected.map((f) => (f.key === key ? { ...f, mode } : f));
        onFieldsChange(next);
    };
    const removeField = (key) => onFieldsChange(selected.filter((f) => f.key !== key));
    const addField = (key) => onFieldsChange([...selected, { key, mode: "optional" }]);
    const move = (i, dir) => {
        const j = i + dir;
        if (j < 0 || j >= selected.length) return;
        const next = [...selected];
        [next[i], next[j]] = [next[j], next[i]];
        onFieldsChange(next);
    };

    return (
        <div className="space-y-3" data-testid="board-apply-form">
            <Field label="Apply mode">
                <PlainSelect
                    value={applyMode}
                    onChange={onApplyMode}
                    options={[
                        ["quick", "Quick apply (Name + Email + Phone + City + Resume)"],
                        ["detailed", "Detailed apply (configurable form below)"],
                    ]}
                    testId="board-apply-mode"
                />
            </Field>
            <Field label="Detail page layout">
                <PlainSelect
                    value={applyLayout || "two_panel"}
                    onChange={onApplyLayout}
                    options={[
                        ["two_panel", "Two panel (JD + apply form side-by-side)"],
                        ["one_panel", "One panel (hide JD when Apply is clicked)"],
                    ]}
                    testId="board-apply-layout"
                />
            </Field>
            <p className="text-[11px] text-foreground/70 -mt-1">
                Resume upload is <strong>always required</strong>. In detailed mode the applicant lands on a form with the fields below in the order shown; in quick mode the candidate applies inline on the JD card.
            </p>
            {applyMode === "detailed" && (
                <div className="border border-border bg-secondary/30 p-3 space-y-3" data-testid="board-apply-fields">
                    {!catalog ? (
                        <div className="text-xs font-mono text-muted-foreground">Loading fields…</div>
                    ) : (
                        <>
                            <div>
                                <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-1.5">
                                    Selected fields ({selected.length}) — drag-equivalent order
                                </div>
                                {selected.length === 0 ? (
                                    <div className="border border-dashed border-border bg-white p-3 text-[11px] text-muted-foreground text-center">
                                        No fields selected yet. Add fields from the picker below.
                                    </div>
                                ) : (
                                    <ul className="space-y-1.5" data-testid="board-apply-selected">
                                        {selected.map((f, i) => (
                                            <li key={f.key} className="flex items-center gap-2 border border-border bg-white px-2 py-1.5" data-testid={`board-apply-row-${f.key}`}>
                                                <span className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground w-6 shrink-0">#{i + 1}</span>
                                                <div className="flex flex-col gap-px shrink-0">
                                                    <button
                                                        type="button"
                                                        onClick={() => move(i, -1)}
                                                        disabled={i === 0}
                                                        className="w-5 h-3 inline-flex items-center justify-center border border-border bg-white hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed"
                                                        aria-label="Move up"
                                                        data-testid={`board-apply-up-${f.key}`}
                                                    ><ChevronUp className="w-3 h-3" /></button>
                                                    <button
                                                        type="button"
                                                        onClick={() => move(i, +1)}
                                                        disabled={i === selected.length - 1}
                                                        className="w-5 h-3 inline-flex items-center justify-center border border-border bg-white hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed"
                                                        aria-label="Move down"
                                                        data-testid={`board-apply-down-${f.key}`}
                                                    ><ChevronDown className="w-3 h-3" /></button>
                                                </div>
                                                <span className="text-xs flex-1 truncate" title={byKey[f.key]?.label}>{byKey[f.key]?.label || f.key}</span>
                                                <select
                                                    value={f.mode}
                                                    onChange={(e) => setMode(f.key, e.target.value)}
                                                    className="text-[10px] font-mono uppercase tracking-widest border border-border bg-white h-7 px-1 shrink-0"
                                                    data-testid={`board-apply-field-${f.key}`}
                                                >
                                                    <option value="optional">Optional</option>
                                                    <option value="required">Required</option>
                                                </select>
                                                <button
                                                    type="button"
                                                    onClick={() => removeField(f.key)}
                                                    className="text-muted-foreground hover:text-destructive shrink-0"
                                                    aria-label="Remove field"
                                                    data-testid={`board-apply-remove-${f.key}`}
                                                ><X className="w-3.5 h-3.5" /></button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            {available.length > 0 && (
                                <div>
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-1.5">
                                        Add field
                                    </div>
                                    <div className="flex flex-wrap gap-1.5" data-testid="board-apply-available">
                                        {available.map((k) => (
                                            <button
                                                key={k}
                                                type="button"
                                                onClick={() => addField(k)}
                                                className="text-[11px] border border-border bg-white px-2 py-1 hover:bg-secondary inline-flex items-center gap-1"
                                                data-testid={`board-apply-add-${k}`}
                                            >
                                                <Plus className="w-3 h-3" /> {byKey[k]?.label || k}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
